import { useState, useRef, useEffect } from 'react';
import './CustomSelect.css';

/* ─────────────────────────────────────────────────────────────
   CUSTOM SELECT — fully styled dropdown that replaces the native
   <select> element. The browser can't touch the dropdown list
   so we get complete control over hover, selected, and theme.

   Props:
   - value       (string)  — currently selected value
   - onChange    (fn)      — called as onChange(value)
   - options     (array)   — [{ value, label }, ...]
   - placeholder (string)  — shown when value is empty
   - disabled    (bool)    — grays out and blocks interaction
   - className   (string)  — extra class on the trigger wrapper
   ───────────────────────────────────────────────────────────── */
function CustomSelect({
  value       = '',
  onChange,
  options     = [],
  placeholder = 'Select…',
  disabled    = false,
  className   = '',
}) {
  const [open, setOpen] = useState(false);
  const wrapRef         = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Close on ESC
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  function handleSelect(optValue) {
    onChange?.(optValue);
    setOpen(false);
  }

  // Find the label for the currently selected value
  const selectedLabel = options.find(o => o.value === value)?.label;

  return (
    <div
      ref={wrapRef}
      className={`cs-wrap ${className}`}
    >
      {/* ── Trigger button ───────────────────────────────────── */}
      <button
        type="button"
        className={[
          'cs-trigger',
          open     ? 'cs-trigger--open'     : '',
          disabled ? 'cs-trigger--disabled' : '',
          !selectedLabel ? 'cs-trigger--placeholder' : '',
        ].filter(Boolean).join(' ')}
        onClick={() => !disabled && setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className="cs-trigger-label">
          {selectedLabel || placeholder}
        </span>
        <span className={`cs-chevron ${open ? 'cs-chevron--up' : ''}`} aria-hidden="true" />
      </button>

      {/* ── Dropdown list ────────────────────────────────────── */}
      {open && (
        <ul className="cs-list" role="listbox">
          {options.map(opt => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={[
                'cs-option',
                opt.value === value ? 'cs-option--selected' : '',
                opt.value === ''    ? 'cs-option--placeholder' : '',
              ].filter(Boolean).join(' ')}
              onMouseDown={() => handleSelect(opt.value)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CustomSelect;
